var App = App || {};

App.Header = (function () {
    'use strict';

    // ========================================================================
    // Configuration
    // ========================================================================

    const CONFIG = {
        SELECTOR_NAV: 'nav.header__nav__primary > ul',
        SELECTOR_TRIGGERS: 'nav.header__nav__primary > ul > li > a, nav.header__nav__primary > ul > li > button',
        SELECTOR_MENUS: 'nav.header__nav__primary > ul .mega-menu',
        ANIMATION_DURATION: 250,
        ANIMATION_EASING: 'ease',
        TIMING_INTENT_DELAY: 400,
        get animationTransition() {
            return `max-height ${this.ANIMATION_DURATION}ms ${this.ANIMATION_EASING}`;
        }
    }

    // ========================================================================
    // State & State Helpers
    // ========================================================================

    const State = {
        menuTimeout: null,
        currentMenu: null,
        interactionKeyboard: false,
        clearMenuTimeout() {
            clearTimeout(this.menuTimeout);
            this.menuTimeout = null;
        }
    };

    // ========================================================================
    // Initialization
    // ========================================================================

    const InitializeMegaMenu = () => {

        const nav = document.querySelector(CONFIG.SELECTOR_NAV);
        if (!nav) return;
        const triggers = nav.querySelectorAll(CONFIG.SELECTOR_TRIGGERS);
        const megaMenus = nav.querySelectorAll(CONFIG.SELECTOR_MENUS);

        Helpers.setMegaMenuContentTabIndex(megaMenus);
        Events.bindMegaMenuEvents(triggers);
        Events.bindResettingEvents(nav, megaMenus);
    };

    // ========================================================================
    // Event Binding
    // ========================================================================

    const Events = {

        bindMegaMenuEvents(triggers) {

            triggers.forEach(trigger => {

                const triggerContainer = trigger.parentElement;
                const megaMenu = triggerContainer.querySelector(CONFIG.SELECTOR_MENUS);

                triggerContainer.addEventListener('mouseover', (event) => {
                    event.stopPropagation();
                    State.interactionKeyboard = false;
                    if (megaMenu) Handlers.megaMenuEngage(megaMenu);
                });

                triggerContainer.addEventListener('focusin', (event) => {
                    event.stopPropagation();
                    State.interactionKeyboard = true;
                    if (megaMenu) Handlers.megaMenuEngage(megaMenu);
                });

                triggerContainer.addEventListener('mouseout', (event) => {
                    event.stopPropagation();
                    if (megaMenu) Handlers.megaMenuDisengage(megaMenu);
                });

                triggerContainer.addEventListener('focusout', (event) => {
                    event.stopPropagation();
                    if (megaMenu) Handlers.megaMenuDisengage(megaMenu);
                });

                triggerContainer.addEventListener('keydown', (event) => {
                    Handlers.triggerKeyDown(event, triggers, megaMenu);
                });
            });
        },

        bindResettingEvents(nav, megaMenus) {
            nav.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    Handlers.megaMenuAbandon(megaMenus);
                }
            });
            nav.addEventListener('mouseleave', (e) => {
                Handlers.megaMenuAbandon(megaMenus);
            });
        }
    };

    // ========================================================================
    // Handlers
    // ========================================================================

    const Handlers = {

        megaMenuEngage(megaMenu) {

            const intentDelay = State.interactionKeyboard === true ? 0 : CONFIG.TIMING_INTENT_DELAY;
            const switchingMenus = State.currentMenu && State.currentMenu !== megaMenu;
            const alreadyShowing = State.currentMenu === megaMenu;
            const animated = !switchingMenus && !alreadyShowing;

            State.clearMenuTimeout();
            State.menuTimeout = setTimeout(() => {
                if (switchingMenus) {
                    Helpers.setAriaExpanded(State.currentMenu, false);
                    Animator.hide(State.currentMenu, animated);
                }
                if (megaMenu) {
                    Helpers.setAriaExpanded(megaMenu, true);
                    Animator.show(megaMenu, animated);
                }
                State.currentMenu = megaMenu;
            }, intentDelay);
        },

        megaMenuDisengage(megaMenu) {
            const intentDelay = State.interactionKeyboard === true ? 0 : CONFIG.TIMING_INTENT_DELAY;
            State.clearMenuTimeout();
            State.menuTimeout = setTimeout(() => {
                Helpers.setAriaExpanded(megaMenu, false);
                Animator.hide(megaMenu, true);
                State.currentMenu = null;
            }, intentDelay);
        },

        megaMenuAbandon(megaMenus) {
            State.clearMenuTimeout();
            State.currentMenu = null;
            State.interactionKeyboard = false;
            megaMenus.forEach(megaMenu => {
                Helpers.setAriaExpanded(megaMenu, false);
                megaMenu.removeAttribute('style');
            });
        },

        triggerKeyDown(event, triggers, megaMenu) {

            const megaMenuLinks = megaMenu?.querySelectorAll('a, button') ?? [];
            const isTrigger = [...triggers].includes(event.target);
            const isMegaMenuLink = [...megaMenuLinks].includes(event.target);

            if (!isTrigger && !isMegaMenuLink)
                return;

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                event.stopPropagation();
                if (isTrigger) {
                    Helpers.keyDownFocusNextItem(event.target, triggers, 'next');
                    return;
                }
                if (isMegaMenuLink) {
                    const target = Helpers.getMegaMenuTrigger(megaMenu);
                    Helpers.keyDownFocusNextItem(target, triggers, 'next');
                    return;
                }
                return;
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                event.stopPropagation();
                if (isTrigger) {
                    Helpers.keyDownFocusNextItem(event.target, triggers, 'prev');
                    return;
                }
                if (isMegaMenuLink) {
                    const target = Helpers.getMegaMenuTrigger(megaMenu);
                    Helpers.keyDownFocusNextItem(target, triggers, 'prev');
                    return;
                }
                return;
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                if (isTrigger && megaMenuLinks.length) {
                    megaMenuLinks[0].focus();
                    return;
                }
                if (isMegaMenuLink) {
                    Helpers.keyDownFocusNextItem(event.target, megaMenuLinks, 'next');
                    return;
                }
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                if (isMegaMenuLink) {
                    Helpers.keyDownFocusNextItem(event.target, megaMenuLinks, 'prev');
                    return;
                }
                return;
            }
        }
    };

    // ========================================================================
    // Accessibility & UI Utilities
    // ========================================================================

    const Helpers = {

        setMegaMenuContentTabIndex(megaMenus) {
            megaMenus.forEach(menu => {
                const items = menu.querySelectorAll('a, button');
                items.forEach(item => {
                    item.setAttribute('tabindex', '-1');
                });
            });
        },

        setAriaExpanded(megaMenu, expanded) {
            const trigger = megaMenu?.closest('li')?.querySelector(CONFIG.SELECTOR_TRIGGERS);
            if (!trigger) return;
            trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        },

        keyDownFocusNextItem(target, items, direction) {
            let nextIdx = null;
            if (direction === "next")
                nextIdx = ([...items].indexOf(target) + 1) % items.length;
            else if (direction === "prev")
                nextIdx = (([...items].indexOf(target) - 1) + items.length) % items.length;
            if (nextIdx !== -1)
                items[nextIdx].focus();
        },

        getMegaMenuTrigger(megaMenu) {
            return megaMenu.parentElement.querySelector(CONFIG.SELECTOR_TRIGGERS);
        }
    };

    // ========================================================================
    // Mega menu animatior
    // ========================================================================

    const Animator = {

        show(menu, animate = true) {
            menu.style.overflow = "hidden";
            menu.style.display = "block";
            menu.style.visibility = "visible";
            menu.style.transition = CONFIG.animationTransition;
            if (!animate) {
                Animator.showInstant(menu);
                return;
            }
            Animator.showAnimatedBegin(menu);
            menu.addEventListener("transitionend", function handler() {
                Animator.showAnimatedEnd(menu);
                menu.removeEventListener("transitionend", handler);
            });
            menu.addEventListener("transitionend", Animator.showAnimatedEnd);
        },

        showInstant(menu) {
            menu.style.maxHeight = menu.scrollHeight + "px";
            menu.style.transition = "none";
            menu.style.maxHeight = "none";
            menu.style.height = "auto";
        },

        showAnimatedBegin(menu) {
            menu.style.maxHeight = "0";
            void menu.offsetHeight;
            menu.style.maxHeight = menu.scrollHeight + "px";
        },

        showAnimatedEnd(menu) {
            menu.style.maxHeight = "none";
            menu.style.height = "auto";
            menu.removeEventListener("transitionend", Animator.showAnimatedEnd);
        },

        hide(menu, animate = true) {
            menu.style.overflow = "hidden";
            menu.style.transition = CONFIG.animationTransition;
            if (!animate) {
                Animator.hideInstantStyles(menu);
                return;
            }
            Animator.hideAnimatedBegin(menu);
            menu.addEventListener("transitionend", function handler() {
                Animator.hideAnimatedEnd(menu);
                menu.removeEventListener("transitionend", handler);
            });
            menu.addEventListener("transitionend", Animator.hideAnimatedEnd);
        },

        hideInstantStyles(menu) {
            menu.removeAttribute("style");
        },

        hideAnimatedBegin(menu) {
            menu.style.maxHeight = menu.scrollHeight + "px";
            void menu.offsetHeight;
            menu.style.maxHeight = "0";
        },

        hideAnimatedEnd(menu) {
            menu.removeAttribute("style");
            menu.removeEventListener("transitionend", Animator.hideAnimatedEnd);
        }
    };


    // ========================================================================
    // Public API Exposure
    // ========================================================================

    return {
        InitializeMegaMenu
    };

})();
